import { ContextAwareTrafficNode } from './contextAwareTrafficNode';
import { Location } from '../location';
import { CHARGING_STATION_OPERATIONAL_STATUS, CHARGING_STATION_ACCESS_TYPE, TRAFFICNODE_TYPE } from '../../enums/model.enums';
import { HourlyWeather } from '../weather/hourlyWeather';
import { Line } from '../line';
/**
 * class {ChargingStation}
 * should be used to display trafficNode and added information for a charging station
 * @extends {ContextAwareTrafficNode}
 */
export class ChargingStation extends ContextAwareTrafficNode {
    /**
     * constructor of class
     * please use factory instead
     * @param id
     * @param name
     * @param city
     * @param type
     * @param location
     * @param weather
     * @param distance
     * @param servingLines
     * @param zipCode
     * @param address
     * @param operatorInfo
     * @param usageRequirements
     * @param connectors
     * @param operationalStatus
     * @param accessType
     * @param metaData
     */
    constructor(id, name, city, type, location, weather, distance, servingLines, zipCode, address, operatorInfo, usageRequirements, connectors, operationalStatus, accessType, metaData) {
        super(id, name, city, type, location, weather, distance, servingLines, metaData);
        this._zipCode = zipCode;
        this._address = address;
        this._operatorInfo = operatorInfo;
        this._usageRequirements = usageRequirements;
        this._connectors = connectors;
        this._operationalStatus = operationalStatus;
        this._accessType = accessType;
    }
    /**
     * Factory method to create an instance of this class without
     * having to correctly fill the constructor.
     * !!! You should always use this instead of the constructor !!!
     * @param values
     * @returns {ChargingStation}
     */
    static factory(values) {
        const sanitizedValues = {};
        sanitizedValues.id = values.id || '';
        sanitizedValues.name = values.name || '';
        sanitizedValues.city = values.city || '';
        sanitizedValues.type = values.type !== undefined ? values.type : TRAFFICNODE_TYPE.ANY;
        sanitizedValues.location = values.location ? Location.factory(values.location) : Location.factory({});
        sanitizedValues.weather = [];
        if (values.weather) {
            for (const weather of values.weather) {
                sanitizedValues.weather.push(HourlyWeather.factory(weather));
            }
        }
        sanitizedValues.distance = values.distance !== undefined ? values.distance : 0;
        sanitizedValues.servingLines = [];
        if (values.servingLines) {
            for (const servingLine of values.servingLines) {
                sanitizedValues.servingLines.push(Line.factory(servingLine));
            }
        }
        sanitizedValues.zipCode = values.zipCode !== undefined ? values.zipCode : '';
        sanitizedValues.address = values.address !== undefined ? values.address : '';
        sanitizedValues.operatorInfo = values.operatorInfo !== undefined ? values.operatorInfo : { name: '', website: '', email: '', phoneNumber: '' };
        sanitizedValues.usageRequirements = values.usageRequirements !== undefined ? values.usageRequirements : [];
        sanitizedValues.connectors = values.connectors !== undefined ? values.connectors : [];
        sanitizedValues.operationalStatus
            = values.operationalStatus !== undefined ? values.operationalStatus : CHARGING_STATION_OPERATIONAL_STATUS.NO_INFO;
        sanitizedValues.accessType = values.accessType !== undefined ? values.accessType : CHARGING_STATION_ACCESS_TYPE.UNKNOWN;
        sanitizedValues.metaData = values.metaData !== undefined ? values.metaData : {};
        return new ChargingStation(sanitizedValues.id, sanitizedValues.name, sanitizedValues.city, sanitizedValues.type, sanitizedValues.location, sanitizedValues.weather, sanitizedValues.distance, sanitizedValues.servingLines, sanitizedValues.zipCode, sanitizedValues.address, sanitizedValues.operatorInfo, sanitizedValues.usageRequirements, sanitizedValues.connectors, sanitizedValues.operationalStatus, sanitizedValues.accessType, sanitizedValues.metaData);
    }
    /**
     * get the totalNumberOfConnectors
     * @returns number
     */
    get totalNumberOfConnectors() {
        if (this._connectors !== undefined) {
            return this._connectors.length;
        }
        else {
            return undefined;
        }
    }
    /**
     * get the numberOfOperationalConnectors
     * @returns number
     */
    get numberOfOperationalConnectors() {
        if (this._connectors !== undefined) {
            return this._connectors.filter((connector) => connector.operationalStatus === CHARGING_STATION_OPERATIONAL_STATUS.OPERATIONAL).length;
        }
        else {
            return undefined;
        }
    }
    /**
     * get the zip code for this charging station
     * @returns {string}
     */
    get zipCode() {
        return this._zipCode;
    }
    /**
     * set the zip code for this charging station
     * @param value
     */
    set zipCode(value) {
        this._zipCode = value;
    }
    /**
     * get the address
     * @returns string
     */
    get address() {
        return this._address;
    }
    /**
     * set the address
     * @param value
     */
    set address(value) {
        this._address = value;
    }
    /**
     * get the operatorInfo
     * @returns variableType
     */
    get operatorInfo() {
        return this._operatorInfo;
    }
    /**
     * set the operatorInfo
     * @param value
     */
    set operatorInfo(value) {
        this._operatorInfo = value;
    }
    /**
     * get the usageRequirements
     * @returns CHARGING_STATION_USAGE_REQUIREMENT[]
     */
    get usageRequirements() {
        return this._usageRequirements;
    }
    /**
     * set the usageRequirements
     * @param value
     */
    set usageRequirements(value) {
        this._usageRequirements = value;
    }
    /**
     * get the connector information for the charging station
     * @returns ChargingStationConnector[]
     */
    get connectors() {
        return this._connectors;
    }
    /**
     * get all connectors grouped by space
     * @returns Map<string, Array<ChargingStationConnector>>
     */
    get connectorsGroupedBySpace() {
        const groupedConnectors = new Map();
        for (const connector of this._connectors) {
            if (groupedConnectors.has(connector.spaceID)) {
                groupedConnectors.get(connector.spaceID).push(connector);
            }
            else {
                groupedConnectors.set(connector.spaceID, new Array(connector));
            }
        }
        return groupedConnectors;
    }
    /**
     * set the connector information for the charging station
     * @param value
     */
    set connectors(value) {
        this._connectors = value;
    }
    /**
     * get the operational status information for the charging station
     * @returns CHARGING_STATION_OPERATIONAL_STATUS
     */
    get operationalStatus() {
        return this._operationalStatus;
    }
    /**
     * set the operational status information for the charging station
     * @param value
     */
    set operationalStatus(value) {
        this._operationalStatus = value;
    }
    /**
     * get the access type for the charging station
     * @returns CHARGING_STATION_ACCESS_TYPE
     */
    get accessType() {
        return this._accessType;
    }
    /**
     * set the access type for the charging station
     * @param value
     */
    set accessType(value) {
        this._accessType = value;
    }
}
//# sourceMappingURL=chargingStation.js.map